import React, { useState, useEffect, useRef, useCallback } from "react"
import { Button, Modal } from "reactstrap"
import {
  CheckCircle2,
  Lock,
  ListChecks,
  AlertTriangle,
  Clock3,
  Trophy,
  ArrowRight,
  X,
} from "lucide-react"
import axios from "axios"
import { useHistory } from "react-router-dom"
import { isMobile } from "react-device-detect"
import "./QuizModal.css"
import { quizData } from "./quizData"

// ── Scoring rules ───────────────────────────────────────────────────────────
// Correct answer : +10
// Wrong answer   :  0  (no negative marking)
// No answer      :  0
const POINTS_CORRECT = 10
const POINTS_WRONG = 0
const POINTS_UNANSWERED = 0

// Seconds each question stays on screen before it auto-advances.
const QUESTION_SECONDS = 15

// Duration of the "3, 2, 1, GO" intro shown before the first question of a
// quiz becomes readable/interactive. Must match CountdownOverlay's 4 slots.
const COUNTDOWN_MS = 3600

// ── Quiz availability cutoff ────────────────────────────────────────────────
// Quizzes close at 30 Sep 2026, 4:00 PM IST. The "+05:30" pins this to IST no
// matter what timezone the visitor's device is set to.
//
// 🔧 TO TEST: change the date/time below, then reload the quiz page.
//   • A time a couple of minutes from now, e.g. "2026-09-29T22:05:00+05:30",
//     lets you watch the grid flip to the "closed" screen live.
//   • Any time already in the past, e.g. "2026-09-29T21:00:00+05:30", shows
//     the closed screen immediately.
//   ⚠️ Put it back to "2026-09-30T16:00:00+05:30" before deploying.
const QUIZ_CLOSES_AT = new Date("2026-09-30T16:00:00+05:30").getTime()

// Where the "View Your Results" button on the closed screen takes the user.
const RESULTS_ROUTE = "/walmartAnalysis"

// The Lb (LeaderBoard) collection keys everything by an opaque "id" string
// and a "gname" (game name). Every quiz section gets its own gname
// (Quiz_<slug>, set in quizData.js) so it shows up as its own row in that
// user's `scores` array — that's also how we detect "already played" without
// needing any new backend routes: we just look for that gname in the existing
// /playcheck response, the same way AgendaModal reads `obj.Intrested`.
const buildLbId = obj => {
  // Adjust these two fields if your Register schema uses different keys —
  // this mirrors the `${Email}|${name}` convention used server-side in
  // adminpanelapihit() / saveScore().
  const email = obj?.Email || obj?.EmailAddress || obj?._id
  const name = obj?.Firstname || obj?.name || "guest"
  return `${email}|${name}`
}

// ── Background blobs (reused visual language from AgendaModal) ─────────────
const BackgroundBlobs = () => (
  <div
    aria-hidden="true"
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0,
    }}
  >
    <div
      style={{
        position: "absolute",
        top: "-80px",
        right: "-40px",
        width: "260px",
        height: "520px",
        background:
          "linear-gradient(135deg, transparent 0%, rgba(43,127,255,0.07) 30%, rgba(90,173,255,0.2) 50%, rgba(43,127,255,0.07) 70%, transparent 100%)",
        transform: "rotate(-18deg)",
        animation: "beamDrift 8s ease-in-out infinite alternate",
      }}
    />
    <div
      style={{
        position: "absolute",
        bottom: "-60px",
        left: "-60px",
        width: "280px",
        height: "280px",
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(10,42,110,0.6) 0%, transparent 70%)",
      }}
    />
  </div>
)

// ── Quiz selection card ──────────────────────────────────────────────────────
// Completed cards stay fully readable and clickable — clicking one opens
// AlreadyPlayedPopup with the score instead of starting the quiz.
const QuizCard = ({ quiz, idx, attempted, scoreEntry, onSelect }) => (
  <div className="quiz-card" style={{ "--i": idx }}>
    <button
      type="button"
      className={`quiz-card-inner${attempted ? " is-attempted" : " is-available"}`}
      onClick={() => onSelect(quiz)}
    >
      <div className="quiz-card-top">
        <span className="quiz-card-title">{quiz.title}</span>
        {attempted ? (
          <span className="quiz-badge quiz-badge-done">
            <CheckCircle2 size={13} />
            Completed
          </span>
        ) : (
          <span className="quiz-badge quiz-badge-open">
            <ListChecks size={13} />
            {quiz.questionCount} Qs
          </span>
        )}
      </div>
      <div className="quiz-card-bottom">
        {attempted ? (
          <span className="quiz-card-cta quiz-card-cta-locked">
            <span>
              <Lock size={13} />
            </span>
             Scored -
            <p>
            {typeof scoreEntry?.score === "number" ? `${scoreEntry.score} pts` : ""}
            </p>
          </span>
        ) : (
          <span className="quiz-card-cta">
            Play Quiz
            <ArrowRight size={13} />
          </span>
        )}
      </div>
    </button>
  </div>
)

// ── Instructions / consent popup ─────────────────────────────────────────────
const InstructionsPopup = ({ quiz, onCancel, onConfirm }) => {
  const [consent, setConsent] = useState(false)

  return (
    <Modal isOpen centered className="quiz-instructions-modal">
      <div className="quiz-instructions-body">
        <div className="quiz-instructions-header">
          <span className="quiz-instructions-title">{quiz.title}</span>
          {/* <button
            type="button"
            className="quiz-close-btn"
            onClick={onCancel}
            aria-label="Close"
          >
            <X size={18} />
          </button> */}
        </div>

        <div className="quiz-instructions-section">
          <span className="quiz-section-label">Quiz Instructions</span>
          <ul className="quiz-instructions-list">
            <li>This quiz has multiple choice or true/false questions.</li>
            <li>Each question has only one correct option.</li>
            <li>
              You have {QUESTION_SECONDS} seconds to answer each question —
              once the timer runs out, it automatically moves to the next one
              and you can't go back.
            </li>
            <li>Correct answer: <strong>+{POINTS_CORRECT} points</strong></li>
            <li>Unanswered question: <strong>{POINTS_UNANSWERED} points</strong></li>
            <li>Once you finish or leave, this quiz cannot be replayed.</li>
          </ul>
        </div>

        <div className="quiz-important-note">
          <AlertTriangle size={16} className="quiz-important-icon" />
          <span>
            <strong>Important:</strong> Play the quiz only after you visited the experience zone
          </span>
        </div>

        <label className="quiz-consent-row">
          <input
            type="checkbox"
            checked={consent}
            onChange={e => setConsent(e.target.checked)}
          />
          <span>I confirm I attended this session and I'm ready to start.</span>
        </label>

        <div className="quiz-instructions-actions">
          <Button
            size="sm"
            className="quiz-btn quiz-btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="quiz-btn quiz-btn-primary"
            disabled={!consent}
            onClick={onConfirm}
          >
            Start Quiz
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ── Leave-quiz warning popup (route change / close attempt mid-quiz) ────────
const LeaveWarningPopup = ({ onStay, onLeave, submitting }) => (
  <Modal isOpen centered className="quiz-leave-modal">
    <div className="quiz-instructions-body">
      <div className="quiz-instructions-header">
        <span className="quiz-instructions-title">Leave this quiz?</span>
      </div>
      <div className="quiz-important-note">
        <AlertTriangle size={16} className="quiz-important-icon" />
        <span>
          Your score will be saved <strong>as it is right now</strong>, and
          any questions you haven't answered yet will be marked with{" "}
          <strong>0 points</strong>. This quiz cannot be replayed afterwards.
        </span>
      </div>
      <div className="quiz-instructions-actions">
        <Button size="sm" className="quiz-btn quiz-btn-ghost" onClick={onStay}>
          Stay &amp; Continue
        </Button>
        <Button
          size="sm"
          className="quiz-btn quiz-btn-primary"
          onClick={onLeave}
          disabled={submitting}
        >
          {submitting ? "Saving..." : "Leave & Save Score"}
        </Button>
      </div>
    </div>
  </Modal>
)

// ── "Already played" popup (clicking a completed card in the grid) ─────────
const AlreadyPlayedPopup = ({ quiz, scoreEntry, onClose }) => (
  <Modal isOpen centered className="quiz-instructions-modal quiz-already-played-modal">
    <div className="quiz-instructions-body">
      <div className="quiz-instructions-header">
        <span className="quiz-instructions-title">{quiz.title}</span>
        {/* <button
          type="button"
          className="quiz-close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button> */}
      </div>

      <div className="quiz-already-played-body">
        <Lock size={26} className="quiz-already-played-icon" />
        <p className="quiz-already-played-text">
          You have already played this quiz.
        </p>
        <div className="quiz-already-played-score">
          <span className="quiz-already-played-score-num">
            {typeof scoreEntry?.score === "number" ? scoreEntry.score : 0}
          </span>
          <span className="quiz-already-played-score-label">points scored</span>
        </div>
      </div>

      <div className="quiz-instructions-actions">
        <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={onClose}>
          Got it
        </Button>
      </div>
    </div>
  </Modal>
)

// The "3, 2, 1, GO" intro shown before a quiz's first question becomes
// readable/interactive. All four numbers are mounted at once; each one's CSS
// animation-delay staggers its pulse-in/out, so the sequence plays with pure
// CSS instead of JS-driven per-tick state. Total runtime must match
// COUNTDOWN_MS above (4 slots × 900ms = 3600ms).
const CountdownOverlay = () => (
  <div className="quiz-countdown-overlay" aria-hidden="true">
    <span className="quiz-countdown-num" style={{ animationDelay: "0ms" }}>3</span>
    <span className="quiz-countdown-num" style={{ animationDelay: "900ms" }}>2</span>
    <span className="quiz-countdown-num" style={{ animationDelay: "1800ms" }}>1</span>
    <span
      className="quiz-countdown-num quiz-countdown-go"
      style={{ animationDelay: "2700ms" }}
    >
      GO!
    </span>
  </div>
)

// ── Live MCQ player ──────────────────────────────────────────────────────────
// Navigation is timer-driven by default: no Previous/Next buttons and the
// progress dots are a read-only indicator. The learner can pick or change an
// answer freely until the per-question timer hits 0, at which point
// `onTimeUp` fires and the parent either advances to the next question or
// submits the quiz. Once an answer is picked, a manual button also appears
// so the learner isn't forced to sit through the full countdown.
const QuizPlayer = ({
  quiz,
  answers,
  setAnswers,
  currentIndex,
  onTimeUp,
  onSubmit,
  paused,
  countingDown,
}) => {
  const question = quiz.questions[currentIndex]
  const total = quiz.questions.length
  const isLast = currentIndex === total - 1
  const isAnswered = Boolean(answers[question.id])

  const [timeLeft, setTimeLeft] = useState(QUESTION_SECONDS)
  const onTimeUpRef = useRef(onTimeUp)
  onTimeUpRef.current = onTimeUp

  // Reset the countdown only when the question itself changes — never when
  // `paused` toggles, or opening/closing the leave-confirmation dialog would
  // silently reset a partially-elapsed timer back to full.
  useEffect(() => {
    setTimeLeft(QUESTION_SECONDS)
  }, [currentIndex, question.id])

  // Run (or stop) the ticking independently of the reset above. While
  // `paused` is true (the leave-confirmation dialog is open) the interval is
  // simply not (re)started, so `timeLeft` sits frozen at whatever it already
  // was; unpausing resumes counting down from there instead of restarting.
  //
  // The state updater below is kept PURE (it only returns a number). Calling
  // onTimeUp() from inside a setState updater is unsafe — React may run an
  // updater more than once, which can fire the submit twice.
  useEffect(() => {
    if (paused) return undefined
    const t = setInterval(() => {
      setTimeLeft(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => clearInterval(t)
  }, [currentIndex, question.id, paused])

  // Fire onTimeUp exactly once per question when the timer reaches 0.
  const timeUpFiredRef = useRef(false)
  useEffect(() => {
    timeUpFiredRef.current = false
  }, [currentIndex, question.id])
  useEffect(() => {
    if (timeLeft === 0 && !paused && !timeUpFiredRef.current) {
      timeUpFiredRef.current = true
      onTimeUpRef.current()
    }
  }, [timeLeft, paused])

  const isUrgent = timeLeft <= 5

  return (
    <div className="quiz-player">
      {countingDown && <CountdownOverlay />}

      <div className="quiz-player-topbar">
        <span className="quiz-progress-text">
          Question {currentIndex + 1} / {total}
        </span>
        <span className={`quiz-timer${isUrgent ? " is-urgent" : ""}`}>
          <Clock3 size={13} />
          {timeLeft}s left
        </span>
      </div>

      <div className="quiz-progress-track">
        <div
          className={`quiz-progress-fill${isUrgent ? " is-urgent" : ""}`}
          style={{ width: `${(timeLeft / QUESTION_SECONDS) * 100}%` }}
        />
      </div>

      <div className={`quiz-question-block${paused ? " is-paused-blur" : ""}`}>
        <div className="quiz-question-text">{question.prompt}</div>
        <div className="quiz-options">
          {question.options.map(opt => (
            <button
              type="button"
              key={opt.id}
              className={`quiz-option${
                answers[question.id] === opt.id ? " is-selected" : ""
              }`}
              onClick={() =>
                setAnswers(prev => ({ ...prev, [question.id]: opt.id }))
              }
            >
              <span className="quiz-option-letter">
                {opt.id.toUpperCase()}
              </span>
              <span className="quiz-option-text">{opt.text}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="quiz-player-nav">
        <div className="quiz-dots" aria-hidden="true">
          {quiz.questions.map((q, i) => (
            <span
              key={q.id}
              className={`quiz-dot${i === currentIndex ? " is-active" : ""}${
                answers[q.id] ? " is-answered" : ""
              }`}
            />
          ))}
        </div>

        {!countingDown && (
          isLast ? (
            <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={onSubmit}>
              Submit
            </Button>
          ) : (
            isAnswered && (
              <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={onTimeUp}>
                Next
              </Button>
            )
          )
        )}
      </div>
    </div>
  )
}

// ── Shown in place of the quiz grid once QUIZ_CLOSES_AT has passed ──────────
const QuizClosedScreen = ({ onViewResults }) => (
  <div className="quiz-closed">
    <Clock3 size={40} className="quiz-closed-icon" />
    <div className="quiz-closed-title">Quizzes are closed</div>
    <p className="quiz-closed-text">
      The quizzes are not available anymore. View your results to see how you did.
    </p>
    <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={onViewResults}>
      View Your Results
      <ArrowRight size={13} />
    </Button>
  </div>
)

// ── Result screen ────────────────────────────────────────────────────────────
const ResultScreen = ({ result, quiz, onClose }) => (
  <div className="quiz-result">
    <Trophy size={40} className="quiz-result-icon" />
    <div className="quiz-result-score">{result.score} pts</div>
    <div className="quiz-result-quiz-title">{quiz.title}</div>
    <div className="quiz-result-breakdown">
      <div>
        <span className="quiz-result-num quiz-result-correct">
          {result.correct}
        </span>
        Correct
      </div>
      <div>
        <span className="quiz-result-num quiz-result-wrong">
          {result.wrong}
        </span>
        Wrong
      </div>
      <div>
        <span className="quiz-result-num quiz-result-unanswered">
          {result.unanswered}
        </span>
        Unanswered
      </div>
    </div>
    <div className="quiz-result-time">
      Time taken: {Math.floor(result.timeTaken / 60)}m {result.timeTaken % 60}s
    </div>
    <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={onClose}>
      Back to Quizzes
    </Button>
  </div>
)

// ── Main Component ───────────────────────────────────────────────────────────
const QuizModal = ({ isOpen, toggle }) => {
  const history = useHistory()
  const obj = JSON.parse(localStorage.getItem("authUser") || "{}")
  const lbId = buildLbId(obj)

  // Keyed by gname so completed cards can show the score the learner got,
  // not just a yes/no "already played" flag.
  const [scoresByGname, setScoresByGname] = useState({})
  const [view, setView] = useState("list") // list | instructions | playing | result
  const [activeQuiz, setActiveQuiz] = useState(null)
  const [answers, setAnswers] = useState({})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [startedAt, setStartedAt] = useState(null)
  const [result, setResult] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [countingDown, setCountingDown] = useState(false)
  const [alreadyPlayedInfo, setAlreadyPlayedInfo] = useState(null)

  // "loading" | "ok" | "error" — until /playcheck succeeds we DON'T show any
  // quiz as playable, otherwise a failed/slow request makes every card look
  // unplayed and lets people replay (which used to add the score again).
  const [statusState, setStatusState] = useState("loading")

  // Synchronous lock: guarantees one submit per quiz run even if the timer,
  // the Submit button and the Leave dialog all fire in the same instant
  // (React state like `submitting` updates too late to stop that).
  const submitLockRef = useRef(false)

  // True once the cutoff (QUIZ_CLOSES_AT) has passed. Checked on mount, then
  // re-checked every second (see the effect below) so the screen flips at the
  // cutoff without needing a reload.
  const [isClosed, setIsClosed] = useState(() => Date.now() >= QUIZ_CLOSES_AT)

  // Route-leave guard state
  const [pendingLocation, setPendingLocation] = useState(null)
  const [showLeaveWarning, setShowLeaveWarning] = useState(false)
  const unblockRef = useRef(null)
  const viewRef = useRef(view)
  viewRef.current = view

  const adminPanel = isMobile
    ? `${process.env.REACT_APP_ADMIN}`
    : `${process.env.REACT_APP_ADMIN_LAPTOP}`

  // ── Fetch already-played quizzes so their cards render locked ──────────────
  const fetchPlayStatus = useCallback(() => {
    setStatusState("loading")
    return axios
      .get(`${adminPanel}/playcheck`, { params: { id: lbId } })
      .then(res => {
        const scores = res?.data?.scores
        if (!Array.isArray(scores)) throw new Error("Unexpected /playcheck response")
        const map = {}
        scores.forEach(s => {
          map[s.gname] = s
        })
        setScoresByGname(map)
        setStatusState("ok")
      })
      .catch(err => {
        console.error("Failed to fetch quiz attempt status:", err)
        setStatusState("error")
      })
  }, [adminPanel, lbId])

  useEffect(() => {
    if (!isOpen) return
    fetchPlayStatus()
  }, [isOpen])

  // ── Run (and auto-end) the pre-quiz "3, 2, 1, GO" countdown ─────────────────
  // Starting the actual play clock here (rather than at handleConfirmStart)
  // keeps "time taken" from counting the ~3.6s intro against the learner.
  useEffect(() => {
    if (!countingDown) return undefined
    const t = setTimeout(() => {
      setCountingDown(false)
      setStartedAt(Date.now())
    }, COUNTDOWN_MS)
    return () => clearTimeout(t)
  }, [countingDown])

  // ── Compute score from current answers state ────────────────────────────────
  const computeResult = useCallback(
    (quiz, currentAnswers, timeTaken) => {
      let score = 0,
        correct = 0,
        wrong = 0,
        unanswered = 0
      quiz.questions.forEach(q => {
        const given = currentAnswers[q.id]
        if (!given) {
          unanswered += 1
          score += POINTS_UNANSWERED
        } else if (given === q.correct) {
          correct += 1
          score += POINTS_CORRECT
        } else {
          wrong += 1
          score += POINTS_WRONG
        }
      })
      return { score, correct, wrong, unanswered, timeTaken }
    },
    []
  )

  // ── Submit a score to the existing /submitscore endpoint ───────────────────
  const submitScore = (quiz, scoreResult) =>
  axios
    .post(`${adminPanel}/submitscore`, {
      score: scoreResult.score,
      id: lbId,
      gname: quiz.gname,
      timeTaken: scoreResult.timeTaken,
    })
    .then(res => {
      // The server replies with a plain string on validation errors
      if (typeof res.data === "string") {
        throw new Error(res.data)
      }
      setScoresByGname(prev => ({
        ...prev,
        [quiz.gname]: {
          gname: quiz.gname,
          score: scoreResult.score,
          time: scoreResult.timeTaken,
          playedAt: new Date().toISOString(),
        },
      }))
    })

  // ── Route-change guard: block navigation while a quiz is in progress ───────
  useEffect(() => {
    unblockRef.current = history.block(location => {
      if (viewRef.current !== "playing") return true
      setPendingLocation(location)
      setShowLeaveWarning(true)
      return false
    })
    return () => {
      if (unblockRef.current) unblockRef.current()
    }
  }, [history])

  // Warn on tab close / refresh too (browsers show their own generic text)
  useEffect(() => {
    const handler = e => {
      if (viewRef.current === "playing") {
        e.preventDefault()
        e.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [])

  const resetToList = () => {
    setView("list")
    setActiveQuiz(null)
    setAnswers({})
    setCurrentIndex(0)
    setStartedAt(null)
    setResult(null)
    setCountingDown(false)
  }

  // ── Flip to the "closed" state the moment the cutoff passes ────────────────
  // Re-checking Date.now() every second (instead of one long setTimeout) means
  // the screen still catches up immediately if the phone slept or the tab was
  // in the background when the cutoff hit.
  useEffect(() => {
    if (isClosed) return undefined
    const t = setInterval(() => {
      if (Date.now() >= QUIZ_CLOSES_AT) setIsClosed(true)
    }, 1000)
    return () => clearInterval(t)
  }, [isClosed])

  // If the instructions popup happens to be open when the cutoff hits, close
  // it. A quiz that's already being *played* is allowed to finish (it's at
  // most ~45s); the closed screen shows once the learner returns to the list.
  useEffect(() => {
    if (isClosed && viewRef.current === "instructions") resetToList()
  }, [isClosed])

  const goToResults = () => history.push(RESULTS_ROUTE)

  // Completed quizzes are clickable too — they just show the score instead
  // of starting the quiz over.
  const handleSelectQuiz = quiz => {
    if (isClosed) return
    const scoreEntry = scoresByGname[quiz.gname]
    if (scoreEntry) {
      setAlreadyPlayedInfo({ quiz, scoreEntry })
      return
    }
    setActiveQuiz(quiz)
    setView("instructions")
  }

  const handleConfirmStart = () => {
    // Last-second guard in case the cutoff passed while the popup was open.
    if (Date.now() >= QUIZ_CLOSES_AT) {
      setIsClosed(true)
      resetToList()
      return
    }
    submitLockRef.current = false
    setAnswers({})
    setCurrentIndex(0)
    setCountingDown(true)
    setView("playing")
    // startedAt is set once the countdown finishes (see the countingDown
    // effect above), so "time taken" doesn't include the intro animation.
  }

  // Server answered 409: this quiz is already recorded (e.g. an earlier
  // attempt actually saved even though the response never arrived). Reload the
  // real scores and send the learner back to the grid with the card locked.
  const isAlreadyPlayed = err => err?.response?.status === 409

  const handleSubmitQuiz = useCallback(() => {
    if (submitLockRef.current || !activeQuiz || !startedAt) return
    submitLockRef.current = true
    const timeTaken = Math.floor((Date.now() - startedAt) / 1000)
    const scoreResult = computeResult(activeQuiz, answers, timeTaken)
    setSubmitting(true)
    submitScore(activeQuiz, scoreResult)
      .then(() => {
        setResult(scoreResult)
        setView("result")
      })
      .catch(err => {
        if (isAlreadyPlayed(err)) {
          resetToList()
          fetchPlayStatus()
          return
        }
        console.error("Failed to submit quiz score:", err)
        // Safe to allow a retry now: the server refuses a second save.
        submitLockRef.current = false
        alert("Couldn't save your score — please check your connection and try again.")
      })
      .finally(() => setSubmitting(false))
  }, [activeQuiz, answers, startedAt, computeResult, fetchPlayStatus])

  // Called by QuizPlayer when a question's 15s timer runs out: move on, or
  // submit if this was the last question.
  const handleTimeUp = useCallback(() => {
    if (!activeQuiz) return
    setCurrentIndex(i => {
      if (i >= activeQuiz.questions.length - 1) {
        return i
      }
      return i + 1
    })
    if (currentIndex >= activeQuiz.questions.length - 1) {
      handleSubmitQuiz()
    }
  }, [activeQuiz, currentIndex, handleSubmitQuiz])

  const handleLeaveConfirmed = () => {
    if (submitLockRef.current) return
    submitLockRef.current = true
    const timeTaken = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0
    const scoreResult = computeResult(activeQuiz, answers, timeTaken)
    setSubmitting(true)
    submitScore(activeQuiz, scoreResult)
      .catch(err => console.error("Failed to save in-progress quiz score:", err))
      .finally(() => {
        setSubmitting(false)
        setShowLeaveWarning(false)
        resetToList()
        // Reload the real status so the card is locked whether or not the
        // save response made it back.
        fetchPlayStatus()
        // Triggered by an in-app route change (back button, a link) that the
        // history guard intercepted — honor the navigation the user
        // originally asked for. The guard itself doesn't need to be manually
        // released: its predicate already allows navigation once `view` is
        // no longer "playing" (set by resetToList() above), and it stays
        // registered for the next quiz the learner plays in this modal.
        if (pendingLocation) {
          history.push(pendingLocation.pathname + (pendingLocation.search || ""))
          setPendingLocation(null)
        }
        // No pendingLocation means this was triggered by the modal's own
        // close (X) button instead of a route change. The score is already
        // saved, so just leave the learner on the quiz grid (resetToList()
        // above already did that) rather than closing the modal or
        // navigating anywhere.
      })
  }

  const handleStay = () => {
    setShowLeaveWarning(false)
    setPendingLocation(null)
  }

  // Closing the modal itself (X button) while mid-quiz should trigger the
  // same warning instead of silently discarding progress.
  const handleModalToggle = () => {
    if (view === "playing") {
      setPendingLocation(null)
      setShowLeaveWarning(true)
      return
    }
    resetToList()
    toggle()
  }

  return (
    <>
      <style>{`
        @keyframes beamDrift {
          from { opacity: 0.6; transform: rotate(-18deg) translateX(0); }
          to   { opacity: 1;   transform: rotate(-18deg) translateX(20px); }
        }
        @keyframes quizCardFadeIn {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .quiz-card { animation: quizCardFadeIn 0.4s ease both; animation-delay: calc(var(--i,0)*0.06s); }
      `}</style>

      {showLeaveWarning && (
        <LeaveWarningPopup
          onStay={handleStay}
          onLeave={handleLeaveConfirmed}
          submitting={submitting}
        />
      )}

      {view === "instructions" && activeQuiz && (
        <InstructionsPopup
          quiz={activeQuiz}
          onCancel={resetToList}
          onConfirm={handleConfirmStart}
        />
      )}

      {alreadyPlayedInfo && (
        <AlreadyPlayedPopup
          quiz={alreadyPlayedInfo.quiz}
          scoreEntry={alreadyPlayedInfo.scoreEntry}
          onClose={() => setAlreadyPlayedInfo(null)}
        />
      )}

      <Modal isOpen={isOpen} toggle={handleModalToggle} className="quiz-modal">
        <div
          className="quiz-modal-shell"
          style={{
            position: "relative",
            // background:
            //   "linear-gradient(175deg, rgb(6, 19, 48) 0%, rgba(2, 11, 26, 0.24) 45%, rgba(0, 0, 0, 0.76) 100%)",
            minHeight: "95vh",
            overflow: "hidden",
          }}
        >
          {/* <BackgroundBlobs /> */}

          <div
            style={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px 14px",
              borderBottom: "1px solid rgba(90,173,255,0.15)",
            }}
          >
            <span className="quiz-header-title">
              {view === "playing" || view === "result" ? activeQuiz?.title : "Quiz"}
            </span>
            <button
              onClick={handleModalToggle}
              style={{
                background: "none",
                border: "none",
                color: "var(--converge-walmart)",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                textShadow: "0 0 8px rgba(43,127,255,0.6)",
                padding: "0 2px",
              }}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="quiz-body" style={{ position: "relative", zIndex: 5 }}>
            {/* After the cutoff: no quiz cards, just the "closed" message
                with a button to the results page. */}
            {view === "list" && isClosed && (
              <QuizClosedScreen onViewResults={goToResults} />
            )}

            {view === "list" && !isClosed && statusState === "loading" && (
              <div className="quiz-closed">
                <p className="quiz-closed-text">Loading quizzes…</p>
              </div>
            )}

            {view === "list" && !isClosed && statusState === "error" && (
              <div className="quiz-closed">
                <AlertTriangle size={32} className="quiz-closed-icon" />
                <p className="quiz-closed-text">
                  Couldn't load your quiz status. Please check your connection.
                </p>
                <Button size="sm" className="quiz-btn quiz-btn-primary" onClick={fetchPlayStatus}>
                  Try again
                </Button>
              </div>
            )}

            {view === "list" && !isClosed && statusState === "ok" && (() => {
              const availableQuizzes = quizData.filter(q => !scoresByGname[q.gname])
              const completedQuizzes = quizData.filter(q => scoresByGname[q.gname])
              return (
                <div className="quiz-grid">
                  {availableQuizzes.map((quiz, idx) => (
                    <QuizCard
                      key={quiz.id}
                      quiz={quiz}
                      idx={idx}
                      attempted={false}
                      onSelect={handleSelectQuiz}
                    />
                  ))}

                  {availableQuizzes.length > 0 && completedQuizzes.length > 0 && (
                    <div className="quiz-divider" aria-hidden="true" />
                  )}

                  {completedQuizzes.map((quiz, idx) => (
                    <QuizCard
                      key={quiz.id}
                      quiz={quiz}
                      idx={availableQuizzes.length + idx}
                      attempted
                      scoreEntry={scoresByGname[quiz.gname]}
                      onSelect={handleSelectQuiz}
                    />
                  ))}
                </div>
              )
            })()}

            {view === "playing" && activeQuiz && (
              <QuizPlayer
                quiz={activeQuiz}
                answers={answers}
                setAnswers={setAnswers}
                currentIndex={currentIndex}
                onTimeUp={handleTimeUp}
                onSubmit={handleSubmitQuiz}
                paused={showLeaveWarning || countingDown}
                countingDown={countingDown}
              />
            )}

            {view === "result" && result && activeQuiz && (
              <ResultScreen result={result} quiz={activeQuiz} onClose={() => { resetToList() }} />
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default QuizModal